import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useMemo, useEffect } from "react";
import classnames from "../../utils/classnames.js";
import "../Grid/Col.js";
import Row from "../Grid/Row.js";
import './Markers.css';import '../Grid/Grid.css';/* empty css                */
import Icon from "../Icon/Icon.js";
import Tag from "../Tag/Tag.js";
import Tooltip from "../Tooltip/Tooltip.js";
import Typography from "../Typography/Typography.js";
/* empty css             */
function getTooltipItemsOverLimit(items, limit, className) {
  let excessiveItems = [];
  if (limit && limit > 0) {
    excessiveItems = items.slice(limit);
  }
  if (excessiveItems.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(Row, { children: /* @__PURE__ */ jsx(
    "ul",
    {
      className: classnames("perxis-auto-markers__more-list", {
        [`${className}__more-list`]: className
      }),
      children: excessiveItems.map((item) => /* @__PURE__ */ jsx("li", { children: item.label }, item.key))
    }
  ) });
}
function calculateTooltipArrowPlacement(containerWidth, markerWidths) {
  let markerRight = 0;
  const tooltipArrowLocations = [];
  for (const markerWidth of markerWidths) {
    if (markerRight + markerWidth > containerWidth) {
      markerRight = 0;
    }
    const markerCenter = markerRight + markerWidth / 2;
    const markerOffset = markerCenter / containerWidth;
    if (markerOffset < 0.33) {
      tooltipArrowLocations.push("bottomLeft");
    } else if (markerOffset > 0.33 && markerOffset < 0.66) {
      tooltipArrowLocations.push("bottom");
    } else {
      tooltipArrowLocations.push("bottomRight");
    }
    markerRight += markerWidth;
  }
  return tooltipArrowLocations;
}
const Markers = ({
  items,
  mode = "singleLineDynamic",
  limit = 3,
  tooltipTitle,
  hasTouchListener,
  className
}) => {
  const isResizeObserverSupported = "ResizeObserver" in window;
  const currentMode = !isResizeObserverSupported && mode === "singleLineDynamic" ? "singleLineLimit" : mode;
  const markersContainerRef = useRef(null);
  const moreIconRef = useRef(null);
  const [visibleMarkersCount, setVisibleMarkersCount] = useState(() => {
    switch (currentMode) {
      case "singleLineLimit":
        return limit;
      case "singleLineDynamic":
        return 0;
      default:
        return items.length;
    }
  });
  const [markersTooltipPlacement, setMarkersTooltipPlacement] = useState([]);
  const moreTagsTooltipContent = useMemo(
    () => getTooltipItemsOverLimit(items, visibleMarkersCount, className),
    [className, items, visibleMarkersCount]
  );
  const calculateVisibleItems = useMemo(
    () => (containerWidth, markers) => {
      if (!markersContainerRef.current || !moreIconRef.current) {
        return;
      }
      let markersWidthOccupied = 0;
      const markerWidths = [];
      for (const marker of markers) {
        const markerWidth = marker.getBoundingClientRect().width;
        markerWidths.push(markerWidth);
        markersWidthOccupied += markerWidth;
      }
      let isMoreIconRequired = false;
      if (currentMode === "singleLineDynamic") {
        isMoreIconRequired = markersWidthOccupied > containerWidth;
      } else if (currentMode === "singleLineLimit") {
        isMoreIconRequired = items.length > limit;
      }
      let availableWidth = containerWidth;
      if (isMoreIconRequired) {
        availableWidth = containerWidth - moreIconRef.current.getBoundingClientRect().width;
      }
      const tooltipArrowsPlacement = calculateTooltipArrowPlacement(availableWidth, markerWidths);
      setMarkersTooltipPlacement(tooltipArrowsPlacement);
      if (currentMode !== "singleLineDynamic") {
        return;
      }
      let visibleMarkersCount2 = 0;
      for (const markerWidth of markerWidths) {
        if (markerWidth > availableWidth) {
          break;
        }
        visibleMarkersCount2 += 1;
        availableWidth -= markerWidth;
      }
      setVisibleMarkersCount(visibleMarkersCount2);
    },
    [currentMode, items.length, limit]
  );
  useEffect(() => {
    if (!isResizeObserverSupported || !markersContainerRef.current) {
      return;
    }
    const resizeObserver = new window.ResizeObserver(
      (entries) => calculateVisibleItems(entries[0].contentRect.width, entries[0].target.children)
    );
    resizeObserver.observe(markersContainerRef.current);
    const markersContainerRect = markersContainerRef.current.getBoundingClientRect();
    const markers = markersContainerRef.current.children;
    calculateVisibleItems(markersContainerRect.width, markers);
    return () => resizeObserver.disconnect();
  }, [calculateVisibleItems, isResizeObserverSupported]);
  const getMarkersToRender = () => {
    if (currentMode === "singleLineLimit" && items.length > limit) {
      return items.slice(0, limit);
    }
    return items;
  };
  const isIconMoreVisible = items.length > visibleMarkersCount;
  const isIconMoreAbsolute = ["singleLineDynamic", "singleLineFit"].includes(currentMode) || currentMode === "singleLineLimit" && !isIconMoreVisible;
  return /* @__PURE__ */ jsxs("div", { className: classnames("perxis-auto-markers", className), children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: markersContainerRef,
        className: classnames([
          "perxis-auto-markers__container",
          {
            "perxis-auto-markers__container--wrap": currentMode === "multiLine",
            "perxis-auto-markers__container--ellipsis": ["singleLineFit", "singleLineLimit", "multiLine"].includes(
              currentMode
            )
          }
        ]),
        children: getMarkersToRender().map(({ key, label, description, ...props }, index) => /* @__PURE__ */ jsx(
          Tooltip,
          {
            destroyTooltipOnHide: true,
            title: description ? /* @__PURE__ */ jsx(Typography.Text, { level: "small", color: "darkest", children: description }) : null,
            hasTouchListener,
            placement: markersTooltipPlacement[index] || "bottom",
            className: classnames("perxis-auto-markers__marker-tooltip", {
              [`${className}__marker-tooltip`]: className
            }),
            triggerClassName: classnames([
              "perxis-auto-markers__marker-tooltip-trigger",
              {
                "perxis-auto-markers__marker-tooltip-trigger--hidden": index + 1 > visibleMarkersCount
              }
            ]),
            buttonClassName: classnames("perxis-auto-markers__marker-tooltip-button"),
            children: /* @__PURE__ */ jsx(Tag, { closable: false, className: classnames("perxis-auto-markers__marker"), ...props, children: label })
          },
          key
        ))
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: classnames([
          "perxis-auto-markers__icon-more",
          {
            "perxis-auto-markers__icon-more--absolute": isIconMoreAbsolute,
            "perxis-auto-markers__icon-more--hidden": !isIconMoreVisible
          }
        ]),
        ref: moreIconRef,
        children: /* @__PURE__ */ jsx(
          Tooltip,
          {
            placement: "bottomRight",
            content: moreTagsTooltipContent,
            title: tooltipTitle ? /* @__PURE__ */ jsx(Typography.Title, { headingLevel: 5, children: tooltipTitle }) : null,
            className: classnames("perxis-auto-markers__more-tooltip", {
              [`${className}__more-tooltip`]: className
            }),
            destroyTooltipOnHide: true,
            hasTouchListener,
            showCloseButton: true,
            children: /* @__PURE__ */ jsx(Icon, { name: "moretags" })
          }
        )
      }
    )
  ] });
};
export {
  Markers as default
};
//# sourceMappingURL=Markers.js.map
